import * as React from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "@/lib/utils";

export function SearchableCombobox({ value = "", onValueChange, options = [], placeholder = "Seleziona...", searchPlaceholder = "Cerca nell'elenco...", emptyText = "Nessun risultato", allowCustom = false, className, disabled = false, testId }) {
  const inputId = React.useId();
  const [open, setOpen] = React.useState(false);
  const normalized = options.map((option) => typeof option === "string" ? { value: option, label: option } : option);
  const selected = normalized.find((option) => option.value === value);
  const [search, setSearch] = React.useState(selected?.label || value || "");
  const rootRef = React.useRef(null);

  React.useEffect(() => {
    if (!open) setSearch(selected?.label || value || "");
  }, [value, selected?.label, open]);

  React.useEffect(() => {
    const close = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const testoSelezionato = selected?.label || value || "";
  const term = open && selected && search === testoSelezionato
    ? ""
    : search.trim().toLocaleLowerCase("it");
  const filtered = normalized.filter((option) =>
    `${option.label} ${option.keywords || ""}`.toLocaleLowerCase("it").includes(term)
  );
  const choose = (option) => {
    onValueChange?.(option.value);
    setSearch(option.label);
    setOpen(false);
  };
  const clear = () => {
    const fallback = normalized.find((option) => ["", "Tutti", "tutti", "__nessuno__", "__none__"].includes(option.value));
    onValueChange?.(fallback?.value || "");
    setSearch(fallback?.label || "");
    setOpen(true);
  };

  const onKeyDown = (event) => {
    if (
      event.key.length === 1
      && !event.ctrlKey
      && !event.metaKey
      && !event.altKey
      && selected
      && search === testoSelezionato
    ) {
      event.preventDefault();
      setSearch(event.key);
      setOpen(true);
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      if (filtered[0]) choose(filtered[0]);
      else if (allowCustom && search.trim()) {
        onValueChange?.(search.trim());
        setOpen(false);
      }
    }
    if (event.key === "Escape") setOpen(false);
  };

  return <div ref={rootRef} className={cn("relative w-full", className)}>
    <div className={cn("relative flex h-10 items-center rounded-md border border-input bg-background shadow-sm focus-within:ring-2 focus-within:ring-ring", disabled && "cursor-not-allowed opacity-50")}>
      <input
        type="text"
        role="combobox"
        aria-expanded={open}
        name={`searchable-list-${inputId.replace(/:/g, "")}`}
        autoComplete="one-time-code"
        autoCorrect="off"
        autoCapitalize="none"
        spellCheck={false}
        data-form-type="other"
        data-lpignore="true"
        data-1p-ignore="true"
        disabled={disabled}
        data-testid={testId}
        value={search}
        placeholder={open ? searchPlaceholder : placeholder}
        onFocus={() => { setSearch(testoSelezionato); setOpen(true); }}
        onClick={() => { if (!open) { setSearch(testoSelezionato); setOpen(true); } }}
        onChange={(event) => { setSearch(event.target.value); setOpen(true); }}
        onKeyDown={onKeyDown}
        className="h-full min-w-0 flex-1 bg-transparent px-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed"
      />
      {value && !disabled ? <button type="button" onClick={clear} className="mr-1 rounded p-1 text-muted-foreground hover:bg-muted" aria-label="Cancella selezione"><X className="h-3.5 w-3.5" /></button> : <ChevronsUpDown className="mr-3 h-4 w-4 shrink-0 opacity-50" />}
    </div>
    {open && !disabled && <div className="absolute z-[70] mt-1 max-h-64 w-full overflow-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-md">
      {filtered.length ? filtered.map((option) => <button type="button" key={option.value || "__empty__"} onMouseDown={(event) => event.preventDefault()} onClick={() => choose(option)} className="flex w-full items-center gap-2 rounded-sm px-2 py-2 text-left text-sm hover:bg-accent">
        <Check className={cn("h-4 w-4 shrink-0", value === option.value ? "opacity-100" : "opacity-0")} />
        <span className="truncate">{option.label}</span>
      </button>) : allowCustom && search.trim() ? <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => { onValueChange?.(search.trim()); setOpen(false); }} className="w-full rounded-sm px-3 py-2 text-left text-sm hover:bg-accent">Usa “{search.trim()}”</button> : <p className="px-3 py-3 text-sm text-muted-foreground">{emptyText}</p>}
    </div>}
  </div>;
}
